import type { OperatingParameters } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import { ref } from "vue";
import type { HypotheticalFlowDataSet, HypotheticalFlowDataRow } from ".";
import { doOneRow } from "./doOneRow";
import { isPumpOutsideGraphingRange } from "@/composables/validation/rules/pumpChecks";

export const doOneSet = (
  hypotheticalFlowRates: number[],
  scenarioCalculations: ScenarioCalculations,
  parameters: OperatingParameters,
  depth: number,
  maxDeltaRequiredPressure: number,
  maxDepth: number
): HypotheticalFlowDataSet => {
  const rows: HypotheticalFlowDataRow[] = [];

  const hypotheticalParameters = ref<OperatingParameters>({
    ...parameters,
    __typename: "scenario_parameters"
  });

  const hypotheticalScenario = {
    ...scenarioCalculations.destructuredScenario,
    parameters: hypotheticalParameters
  };

  const hypotheticalScenarioCalcs =
    useScenarioCalculations(hypotheticalScenario);
  const aggregatePumpCalcs =
    hypotheticalScenarioCalcs.pumpCalculations.aggregate;
  const hypotheticalParameterValues = hypotheticalParameters.value!;

  for (let i = 0; i < hypotheticalFlowRates.length - 1; i++) {
    // prep data
    const previousRow = rows[i - 1];
    const hypotheticalFlowRateSTBD = hypotheticalFlowRates[i];
    hypotheticalParameterValues.target_liquid_rate_std_barrel_day =
      hypotheticalFlowRateSTBD;

    const producedPressurePSIA =
      aggregatePumpCalcs.producedDischargePressurePSIA.value;
    const requiredPressurePSIA =
      aggregatePumpCalcs.requiredDischargePressurePSIA.value;

    // build the current row
    const currentRow = doOneRow(
      hypotheticalFlowRateSTBD,
      producedPressurePSIA,
      requiredPressurePSIA,
      depth,
      maxDeltaRequiredPressure,
      maxDepth,
      isPumpOutsideGraphingRange(
        hypotheticalScenarioCalcs,
        hypotheticalParameterValues
      ),
      previousRow
    );

    rows.push(currentRow);

    const { status } = currentRow;

    if (status === "result-found") {
      // this is the value we are looking for
      return {
        status,
        rows,
        depth,
        targetRow: currentRow
      };
    }
    if (status === "threshold-confirmed") {
      // still have to adjust this to draw from proper row index since
      // we are now going one or more rows beyond threshold-found
      const nextMaxFlow =
        i === 0 || i === 1
          ? hypotheticalFlowRates[0]
          : hypotheticalFlowRates[i - 2];
      const nextMinFlow =
        i === hypotheticalFlowRates.length - 1
          ? hypotheticalFlowRates[hypotheticalFlowRates.length - 1]
          : hypotheticalFlowRates[i + 1];
      const stepSize = (nextMaxFlow - nextMinFlow) / 20;
      const NUM_STEPS = 50;
      const nextHypotheticalFlowRates = Array.from(
        { length: NUM_STEPS },
        (_, i) => i
      ).map(i => nextMaxFlow - i * stepSize);

      const nextDataSet = doOneSet(
        nextHypotheticalFlowRates,
        scenarioCalculations,
        parameters,
        depth + 1,
        maxDeltaRequiredPressure,
        maxDepth
      );

      rows.push(...nextDataSet.rows);

      return {
        ...nextDataSet,
        rows
      };
    }
  }
  const targetRow = rows[rows.length - 1];
  return {
    rows,
    targetRow,
    depth,
    status: targetRow?.status || "error: cannot find healthy flow"
  };
};
